/**
 * How the office got to the Confirm Candidate screen (GOTU-4906).
 *
 * `confirm_candidate:confirm_clicked` already told us a confirmation happened but
 * not what drove it, so it couldn't be joined to the
 * `office_notification:candidate_applied*` events that report how the office was
 * notified. This is that missing link, carried on the confirm event as `source`.
 *
 * Deliberately platform-neutral: the event name prefix (`dental_portal:` vs
 * `mobile:`) already says which app fired it, so a breakdown of `source` by event
 * gives the eight source/platform combinations the ticket asks for without
 * duplicating the platform inside the value. The vocabulary is intentionally
 * identical to the mobile app's copy of this module so one PostHog insight can
 * span both.
 *
 * `Push*` values are mobile-only — kept here so both platforms share one
 * vocabulary, and so a web-side breakdown shows them as an empty bucket rather
 * than as an unknown string.
 */
export const ConfirmCandidateSource = {
  /** Tapped a "candidate applied" push notification. Mobile only. */
  PushCandidateApplied: 'push_candidate_applied',
  /** Tapped an "applied with counter" push notification. Mobile only. */
  PushCandidateAppliedCounter: 'push_candidate_applied_counter',
  /** Followed a "candidate applied" email CTA. */
  EmailCandidateApplied: 'email_candidate_applied',
  /** Followed an "applied with counter" email CTA. */
  EmailCandidateAppliedCounter: 'email_candidate_applied_counter',
  /** Followed a "candidate applied" text CTA. */
  SmsCandidateApplied: 'sms_candidate_applied',
  /** Followed an "applied with counter" text CTA. */
  SmsCandidateAppliedCounter: 'sms_candidate_applied_counter',
  /** Came from an Action Needed list (Dashboard card or Shifts Overview tab). */
  ActionNeeded: 'action_needed',
  /** Came from the Schedule calendar. */
  Schedule: 'schedule',
} as const;

export type TConfirmCandidateSource =
  (typeof ConfirmCandidateSource)[keyof typeof ConfirmCandidateSource];

/**
 * A recorded entry into the confirm flow.
 *
 * `shiftId` scopes the source to the shift the office actually entered for, so a
 * source recorded for one shift can't be reported against a confirmation on a
 * different one (see {@link readConfirmCandidateSource}).
 */
export interface IConfirmCandidateSourceEntry {
  source: TConfirmCandidateSource;
  /** Shift the office entered the flow for. Omit when not known at entry. */
  shiftId?: string;
}
